import { useEffect, useState } from 'react';
import { CustomAxios } from '@shared/api/axiosClient';
import { loadEnvironmentConfig } from '../config/loadEnvironmentConfig';

/** El widget de Zoho se cuelga de window en runtime; TS necesita saberlo. */
declare global {
  interface Window {
    $zoho?: { salesiq?: unknown };
  }
}


export const cleanupZohoSalesIQ = (SCRIPT_ID: string = import.meta.env.VITE_ZOHO_SCRIPT_ID) => {
  if (window?.$zoho) {
    window.$zoho.salesiq = null;
    delete window.$zoho.salesiq;
    delete window.$zoho;
  }

  const initScripts = Array.from(document.getElementsByTagName('script'));
  const zohoInitScript = initScripts.find(script =>
    script.innerHTML.includes('window.$zoho=window.$zoho || {};$zoho.salesiq=$zoho.salesiq')
  );

  if (zohoInitScript) {
    zohoInitScript.remove();
  }

  const scriptIds = [SCRIPT_ID, 'zoho-salesiq-script'];
  scriptIds.forEach(id => {
    const existingScript = document.getElementById(id);
    if (existingScript) {
        console.log(`Script encontrado con ID ${id}:`, existingScript);
        existingScript.remove();
        console.log(`Script con ID ${id} eliminado`);
    }
  });

  const allScripts = Array.from(document.getElementsByTagName('script'));
  allScripts.forEach(script => {
    if (script.src && script.src.includes('salesiq.zohopublic.com')) {
        console.log('Script de Zoho encontrado por URL:', script);
        script.remove();
        console.log('Script de Zoho eliminado por URL');
    }
  });

  const chatContainer = document.querySelector('div[data-id="zsalesiq"]');
  if (chatContainer) {
    chatContainer.remove();
  }
};

const useZohoSalesIQ = () => {
  const { environment, configuration } = loadEnvironmentConfig();
  // loadEnvironmentConfig los declara nullables porque las env vars pueden faltar; este
  // hook ya asumía que están seteadas, y el cast conserva ese supuesto sin tocar el runtime.
  const SCRIPT_ID = configuration.ZOHO_SCRIPT_ID as string;
  const SCRIPT_SRC = configuration.ZOHO_SALES_SCRIPT_SRC as string;
  const department = configuration.ZOHO_DEPARTMENT;
  console.log(`Entorno actual: ${environment}`);
  const [isActiveScript, setIsActiveScript] = useState(false);

  useEffect(() => {
    const checkChatbotStatus = async () => {
      try {
        const { data } = await CustomAxios.get('chatbot');
        setIsActiveScript(data.status);
      } catch (error) {
        console.error('Error checking chatbot status:', error);
        setIsActiveScript(false);
      }
    };

    checkChatbotStatus();
  }, [])

  useEffect(() => {
    if (!isActiveScript) {
      return;
    }

    if (document.getElementById(SCRIPT_ID) && document.querySelector('div[data-id="zsalesiq"]')) {
      return;
    }

    const initZohoSalesScript = document.createElement('script');
    initZohoSalesScript.innerHTML = `
        window.$zoho = window.$zoho || {};
        $zoho.salesiq = $zoho.salesiq || {
            ready: function(callback) {
              if (callback) callback();
            }
        };

        $zoho.salesiq.ready = function () {
            console.log('Zoho SalesIQ está listo');
            console.log('Configurando departamento:', '${department}');

            try {
              $zoho.salesiq.chat.department('${department}');
              console.log('Departamento configurado exitosamente:', '${department}');
            } catch (error) {
              console.error('Error al configurar departamento:', error);
            }
        };
    `;
    document.body.appendChild(initZohoSalesScript);

    const zohoSalesScript = document.createElement('script');
    zohoSalesScript.id = SCRIPT_ID;
    zohoSalesScript.src = SCRIPT_SRC;
    zohoSalesScript.defer = true;
    zohoSalesScript.onload = () => {
          console.log('Script de Zoho SalesIQ cargado exitosamente');
    };
    zohoSalesScript.onerror = (error) => {
        console.error('Error al cargar el script de Zoho SalesIQ:', error);
    };
    document.body.appendChild(zohoSalesScript);

    return () => cleanupZohoSalesIQ(SCRIPT_ID);
  }, [isActiveScript, SCRIPT_ID, SCRIPT_SRC]);
};

export default useZohoSalesIQ;